import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import i18n from './i18n';
import App from './App';
import { configureCloudTts, configurePrebakedAudio } from './speechService';
import type { PrebakedManifest } from './speechService';
import { installGlobalDiagnostics } from '@child/diagnostics';
// Bundle the pre-generated FLOOR manifest at build time (single source of truth,
// written by generate-tts-audio.mjs; the native app imports the same file). On web
// this is the FALLBACK floor: normally the runtime fetch of /tts/manifest.json loads
// the full manifest, but if that fetch fails/races the bundled floor keeps the HD
// floor populated so arcade audio can never go fully robotic. Clips still load from
// /tts/<key>.mp3 at runtime.
import ttsFloorManifest from '../../mobile/src/generated/ttsFloorManifest.json';

// Unlock HTMLAudioElement autoplay on the first user gesture so cloud TTS works
// in the arcade games. speakCloud() fetches audio async — audio.play() then lands
// OUTSIDE the gesture context and the browser blocks it → robotic OS fallback.
// Playing a silent WAV synchronously on the first touch/click marks this origin as
// autoplay-allowed for the rest of the session. Without this the arcade games were
// robotic while the web-child assessment (which already does this) stayed HD.
const _SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
document.addEventListener('pointerdown', () => {
  const a = new Audio(_SILENT_WAV);
  a.volume = 0;
  a.play().catch(() => {});
}, { once: true, capture: true });

// Wire window.onerror + unhandledrejection so uncaught crashes on the parent
// portal (incl. the billing/checkout revenue path) are reported, not silent
// (grand-review P1-14a). web-child + mobile already do this; web-app was blind.
installGlobalDiagnostics();

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// Enable Azure Neural TTS for arcade games / child sessions served from the
// parent portal. Same voice and fallback chain as web-child and mobile.
configureCloudTts({
  endpoint: '/api/media/tts',
  voice: 'en-US-Ava:DragonHDLatestNeural',
  getToken: () =>
    (import.meta.env.VITE_TTS_APP_TOKEN as string | undefined) ||
    localStorage.getItem('child_auth_token') ||
    localStorage.getItem('auth_token') ||
    null,
});

// Prebaked HD floor for arcade-game audio (same fix as web-child): the Dockerfile
// copies mobile's prebaked bundle to /tts so game words play from local HD files
// with no auth — a flaky cloud app-token can never make arcade audio robotic. Cloud
// stays as the fallback for dynamic text. No-ops if the manifest is absent.
// `refresh: true` = seed from the bundled floor NOW, then refresh to the full served
// manifest; a failed/raced fetch keeps the bundled floor so it is never left empty.
//
// CDN-DIRECT base (grand-review P0-1 / P1-1 — the ~2.7× double-hop fix): when
// VITE_MEDIA_CDN is baked at build time, point <audio src> + the manifest fetch at the
// CDN's /tts path so audio bytes go client→CloudFront, taking ECS out of the path
// (mirrors mobile cloudVoice.ts + book art). Unset → same-origin /tts, which nginx now
// 302-redirects to the CDN (see nginx.conf @tts_cdn), so nothing breaks if unwired.
const _mediaCdn = (import.meta.env.VITE_MEDIA_CDN as string | undefined)?.replace(/\/+$/, '');
const _prebakedBase = _mediaCdn ? `${_mediaCdn}/tts` : '/tts';
configurePrebakedAudio({ baseUrl: _prebakedBase, manifest: ttsFloorManifest as PrebakedManifest, refresh: true });

function mount() {
  createRoot(root!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

// Guard render until i18n is confirmed ready — defensive belt-and-suspenders
// in case any future plugin makes initialization asynchronous again.
if (i18n.isInitialized) {
  mount();
} else {
  i18n.on('initialized', mount);
}
